import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sparkles, Send, Loader2, Trash2, Mail, Eye, Paperclip, ChevronRight, X,
  ExternalLink, Upload, FileText, Monitor, Smartphone, User, Plus,
  MailOpen, MousePointerClick, CheckCircle2, Clock, ShieldCheck, TestTube2
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  draft:     { bg: "rgba(107,114,128,0.12)", color: "var(--text-soft)",   label: "Draft" },
  scheduled: { bg: "rgba(245,158,11,0.12)",  color: "#D97706",             label: "Scheduled" },
  sending:   { bg: "rgba(0,68,255,0.12)",    color: "var(--brand)",        label: "Sending" },
  sent:      { bg: "rgba(22,163,74,0.12)",   color: "var(--success)",      label: "Sent" },
};

const MAX_ATTACH_BYTES = 6 * 1024 * 1024;

function humanBytes(n) {
  if (!n) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(n) / Math.log(k)));
  return `${(n / Math.pow(k, i)).toFixed(i ? 1 : 0)} ${sizes[i]}`;
}

function renderPreviewHtml({ subject, body, from = "you@yourbrand.com", to = "sample@contact.com" }) {
  const clean = body || "<em>(empty body)</em>";
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${subject || "Preview"}</title>
<style>
  body{margin:0;background:#F3F4F6;font-family:Inter,system-ui,sans-serif;color:#0F172A}
  .wrap{max-width:640px;margin:24px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px -8px rgba(15,23,42,0.15)}
  .meta{padding:16px 24px;border-bottom:1px solid #E2E8F0;background:#F8FAFC;font-size:13px;color:#64748B}
  .meta .row{margin:2px 0}
  .subject{padding:20px 24px 8px;font-size:20px;font-weight:700}
  .body{padding:8px 24px 32px;line-height:1.6;font-size:15px}
</style></head><body>
<div class="wrap">
  <div class="meta">
    <div class="row"><b style="color:#0F172A">From:</b> ${from}</div>
    <div class="row"><b style="color:#0F172A">To:</b> ${to}</div>
  </div>
  <div class="subject">${subject || "(no subject)"}</div>
  <div class="body">${clean}</div>
</div>
</body></html>`;
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: s.bg, color: s.color }}>
      {status === "sending" && <Loader2 className="w-3 h-3 animate-spin" />}
      {status === "sent" && <CheckCircle2 className="w-3 h-3" />}
      {status === "scheduled" && <Clock className="w-3 h-3" />}
      {s.label}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card className="rise card-premium p-5 flex items-center gap-4"
      style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${color}18` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <div className="stat-number text-2xl" style={{ color: "var(--text)" }}>{value}</div>
        <div className="text-xs font-semibold tracking-wide uppercase mt-0.5" style={{ color: "var(--text-soft)" }}>{label}</div>
      </div>
    </Card>
  );
}

export default function Campaigns() {
  const nav = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const sendPollRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const defaultSenderEmail = (user?.email || "").trim().toLowerCase();
  const defaultSenderName = user?.name || user?.business_name || (defaultSenderEmail ? defaultSenderEmail.split("@")[0] : "LaunchDeck");

  const [form, setForm] = useState({
    name: "",
    subject: "",
    body: "",
    audience_tag: "",
    direct_email: "",
    from_email: defaultSenderEmail,
    from_name: defaultSenderName,
    attachments: []
  });

  const [aiForm, setAiForm] = useState({ topic: "", audience: "general subscribers", tone: "friendly and professional" });
  const [generating, setGenerating] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState("library");
  const [senderInfo, setSenderInfo] = useState(null);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState("");
  const [testSending, setTestSending] = useState(false);

  const loadSenderInfo = () => api.get("/email/sender-info").then(({ data }) => setSenderInfo(data)).catch(() => {});

  const sendTestEmail = async () => {
    if (!testEmailAddr || !testEmailAddr.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!form.body || !form.body.trim()) { toast.error("Add an email body before sending a test"); return; }
    setTestSending(true);
    try {
      const { data } = await api.post("/email/test-send", {
        subject: form.subject,
        body: form.body,
        attachments: form.attachments,
        to_email: testEmailAddr,
        from_email: form.from_email || defaultSenderEmail,
        from_name: form.from_name || defaultSenderName,
      });
      toast.success(`Test sent via ${data.provider} from ${data.sent_from}`);
      setTestEmailOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
    finally { setTestSending(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadSenderInfo(); return () => { if (sendPollRef.current) clearInterval(sendPollRef.current); }; }, []);

  const stats = useMemo(() => ({
    total: items.length,
    sent: items.filter(x => x.status === "sent").length,
    draft: items.filter(x => x.status === "draft").length,
    scheduled: items.filter(x => x.status === "scheduled").length,
  }), [items]);

  const generate = async () => {
    if (!aiForm.topic) { toast.error("Topic required"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/newsletter", aiForm);
      setForm(f => ({
        ...f,
        name: `AI: ${data.subject || aiForm.topic}`.slice(0, 80),
        subject: data.subject || aiForm.topic,
        body: data.html_body || data.plain_body || "",
      }));
      toast.success("Newsletter generated");
      setActiveTab("compose");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!form.name || !form.subject) { toast.error("Name and subject required"); return; }
    const payload = { ...form };
    if (form.direct_email && !form.audience_tag) {
      payload.audience_tag = `__email:${form.direct_email.trim()}`;
    }
    delete payload.direct_email;
    try {
      await api.post("/campaigns", payload);
      setForm({ name: "", subject: "", body: "", audience_tag: "", direct_email: "", from_email: defaultSenderEmail, from_name: defaultSenderName, attachments: [] });
      toast.success("Campaign saved as draft");
      load();
      setActiveTab("library");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveAndSendNow = async () => {
    if (!form.name || !form.subject) { toast.error("Campaign name and subject are required"); return; }
    if (!form.body || !form.body.trim()) { toast.error("Campaign email body cannot be empty"); return; }
    setSendingNow(true);
    const payload = { ...form };
    if (form.direct_email && !form.audience_tag) {
      payload.audience_tag = `__email:${form.direct_email.trim()}`;
    }
    delete payload.direct_email;
    try {
      const { data: created } = await api.post("/campaigns", payload);
      toast.success("Campaign created! Initiating live delivery via Brevo...");
      const { data: sendRes } = await api.post(`/campaigns/${created.id}/send`);
      toast.success(sendRes.message || `Campaign dispatched live from ${payload.from_email || defaultSenderEmail}!`);
      setForm({ name: "", subject: "", body: "", audience_tag: "", direct_email: "", from_email: defaultSenderEmail, from_name: defaultSenderName, attachments: [] });
      load();
      nav(`/campaigns/${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingNow(false);
    }
  };

  const readFileAsDataURL = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Read failed"));
    r.readAsDataURL(file);
  });

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const added = [];
    for (const f of files) {
      if (f.size > MAX_ATTACH_BYTES) { toast.error(`${f.name} exceeds 6 MB`); continue; }
      try {
        const dataUrl = await readFileAsDataURL(f);
        added.push({ name: f.name, type: f.type || "application/octet-stream", size: f.size, url: dataUrl });
      } catch { toast.error(`Failed to read ${f.name}`); }
    }
    if (added.length) {
      setForm(f => ({ ...f, attachments: [...(f.attachments || []), ...added] }));
      toast.success(`${added.length} file${added.length > 1 ? "s" : ""} attached`);
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); };
  const removeAttachment = (i) => setForm(f => ({ ...f, attachments: f.attachments.filter((_, x) => x !== i) }));

  const sendNow = async (id) => {
    const c = items.find(x => x.id === id);
    if (c && !c.audience_tag) {
      if (!window.confirm("No audience tag — this will send to ALL contacts. Continue?")) return;
    }
    try {
      const { data } = await api.post(`/campaigns/${id}/send`);
      toast.success(data.message || "Sending in background");
      if (sendPollRef.current) clearInterval(sendPollRef.current);
      let ticks = 0;
      sendPollRef.current = setInterval(async () => {
        ticks++;
        // Silent refresh — no loading skeleton flicker while polling for completion
        const { data: fresh } = await api.get("/campaigns");
        setItems(fresh);
        const updated = fresh.find(x => x.id === id);
        if (!updated || updated.status !== "sending" || ticks >= 10) {
          clearInterval(sendPollRef.current);
          sendPollRef.current = null;
        }
      }, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    await api.delete(`/campaigns/${id}`); load();
  };

  const previewHtml = useMemo(
    () => renderPreviewHtml({ subject: form.subject, body: form.body, to: form.direct_email || (form.audience_tag ? `contacts tagged "${form.audience_tag}"` : "sample@contact.com") }),
    [form.subject, form.body, form.direct_email, form.audience_tag],
  );

  const openInNewTab = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked — allow popups to preview"); return; }
    w.document.write(previewHtml); w.document.close();
  };

  return (
    <div className="p-6 space-y-6" data-testid="campaigns-page">
      {/* Page header */}
      <div className="rise flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Campaigns</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Generate, schedule and track email campaigns — with live preview.</p>
        </div>
        <Button onClick={() => setActiveTab("compose")} className="gap-2 text-white" style={{ background: "var(--brand)" }}>
          <Plus className="w-4 h-4" /> New Campaign
        </Button>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total" value={stats.total} icon={Mail} color="var(--brand)" />
        <StatCard label="Sent" value={stats.sent} icon={CheckCircle2} color="var(--success)" />
        <StatCard label="Drafts" value={stats.draft} icon={FileText} color="var(--text-soft)" />
        <StatCard label="Scheduled" value={stats.scheduled} icon={Clock} color="#D97706" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="gap-1" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <TabsTrigger value="library" data-testid="tab-library">
            Library {items.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs" style={{ background: "var(--brand-dim)", color: "var(--brand)" }}>{items.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="compose" data-testid="tab-compose">Compose</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" style={{ color: "var(--brand)" }} />AI Generate
          </TabsTrigger>
        </TabsList>

        {/* Library */}
        <TabsContent value="library" className="mt-4">
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="h-20 rounded-xl animate-pulse" style={{ background: "var(--surface)" }} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <Card className="card-premium p-16 text-center" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-20" style={{ color: "var(--text)" }} />
              <div className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>No campaigns yet</div>
              <p className="text-sm mb-6" style={{ color: "var(--text-soft)" }}>Compose your first campaign or let AI generate one for you.</p>
              <Button onClick={() => setActiveTab("ai")} className="gap-2 text-white mx-auto" style={{ background: "var(--brand)" }}>
                <Sparkles className="w-4 h-4" /> Generate with AI
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {items.map((c, idx) => (
                <Card
                  key={c.id}
                  onClick={() => nav(`/campaigns/${c.id}`)}
                  className={`rise card-premium p-5 flex items-center justify-between hover:shadow-lg transition-all cursor-pointer rise-${Math.min(idx + 1, 6)}`}
                  style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                  data-testid={`campaign-card-${c.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: "var(--brand-dim)" }}>
                      <Mail className="w-5 h-5" style={{ color: "var(--brand)" }} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm" style={{ color: "var(--text)" }}>{c.name}</div>
                      <div className="text-xs mt-0.5 truncate max-w-[320px]" style={{ color: "var(--text-soft)" }}>{c.subject}</div>
                      {(c.attachments || []).length > 0 && (
                        <div className="text-xs flex items-center gap-1 mt-1" style={{ color: "var(--text-xsoft)" }}>
                          <Paperclip className="w-3 h-3" /> {c.attachments.length} file{c.attachments.length > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                    {c.status === "sent" && (
                      <div className="hidden md:flex items-center gap-4 mr-2">
                        <div className="text-center">
                          <div className="mono text-sm font-bold" style={{ color: "var(--success)" }}>{c.open_rate || 0}%</div>
                          <div className="text-xs" style={{ color: "var(--text-xsoft)" }}>Opens</div>
                        </div>
                        <div className="text-center">
                          <div className="mono text-sm font-bold" style={{ color: "var(--brand)" }}>{c.click_rate || 0}%</div>
                          <div className="text-xs" style={{ color: "var(--text-xsoft)" }}>Clicks</div>
                        </div>
                        <div className="text-center">
                          <div className="mono text-sm font-bold" style={{ color: "var(--text)" }}>{c.sent_count || 0}</div>
                          <div className="text-xs" style={{ color: "var(--text-xsoft)" }}>Sent</div>
                        </div>
                      </div>
                    )}
                    <StatusBadge status={c.status} />
                    {(c.status === "draft" || c.status === "scheduled") && (
                      <Button data-testid={`send-${c.id}`} size="sm" onClick={() => sendNow(c.id)}
                        className="text-white gap-1.5" style={{ background: "var(--brand)" }}>
                        <Send className="w-3 h-3" /> Send now
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => remove(c.id)}>
                      <Trash2 className="w-4 h-4" style={{ color: "var(--text-xsoft)" }} />
                    </Button>
                    <ChevronRight className="w-4 h-4" style={{ color: "var(--text-xsoft)" }} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Compose */}
        <TabsContent value="compose" className="mt-4">
          {senderInfo && (
            <div className="rounded-xl border p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="sender-info-banner"
              style={{ borderColor: "color-mix(in srgb, var(--success) 30%, var(--border))", background: "color-mix(in srgb, var(--success) 6%, transparent)" }}>
              <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: "var(--success)" }} />
              <span className="text-sm">
                Sending from <strong data-testid="sender-email-display">{senderInfo.sender_name} &lt;{senderInfo.sender_email}&gt;</strong>
              </span>
              <div className="ml-auto flex items-center gap-2">
                {testEmailOpen ? (
                  <>
                    <Input data-testid="test-email-input" value={testEmailAddr} onChange={(e) => setTestEmailAddr(e.target.value)} placeholder="you@email.com" className="h-8 w-48" />
                    <Button size="sm" data-testid="test-email-send-btn" onClick={sendTestEmail} disabled={testSending} className="h-8 text-white gap-1" style={{ background: "var(--brand)" }}>
                      {testSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => setTestEmailOpen(false)}><X className="w-3 h-3" /></Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid="test-email-open-btn" onClick={() => setTestEmailOpen(true)}>
                    <TestTube2 className="w-3.5 h-3.5" /> Send test email
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card className="card-premium p-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <h3 className="font-bold text-base mb-4" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Campaign details</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Campaign name</Label>
                  <Input data-testid="campaign-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="March newsletter — VIPs" />
                </div>
                <div className="col-span-2">
                  <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-500">
                    <Mail className="w-3.5 h-3.5 text-blue-600" />
                    <span>Send From Mailbox Account</span>
                  </Label>
                  <div
                    data-testid="campaign-sender-locked"
                    className="w-full mt-1 h-9 rounded-md border px-3 text-xs font-medium flex items-center justify-between"
                    style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--text)" }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{defaultSenderName}</span>
                      <span className="font-mono text-slate-500 text-[11px]">&lt;{defaultSenderEmail}&gt;</span>
                    </div>
                    <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                      Verified Sender
                    </span>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>Subject line <span className="text-xs font-normal" style={{ color: "var(--text-soft)" }}>(supports {'{{first_name}}'}, {'{{company}}'})</span></Label>
                  <Input data-testid="campaign-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Your March update" />
                </div>
                <div>
                  <Label>Audience tag</Label>
                  <Input data-testid="campaign-audience" value={form.audience_tag} onChange={(e) => setForm({ ...form, audience_tag: e.target.value, direct_email: "" })} placeholder="e.g. vip, lead" />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><User className="w-3 h-3" /> Send to one email</Label>
                  <Input data-testid="campaign-direct-email" type="email" value={form.direct_email} onChange={(e) => setForm({ ...form, direct_email: e.target.value, audience_tag: "" })} placeholder="alex@northwind.io" />
                </div>
                <div className="col-span-2">
                  <Label>Body (rich text)</Label>
                  <RichTextEditor
                    value={form.body}
                    onChange={(html) => setForm({ ...form, body: html })}
                    placeholder="Hi {{first_name}}, here's what we have for you this month…"
                    minHeight={340}
                  />
                </div>
              </div>

              {/* Attachments */}
              <div className="mt-5">
                <Label className="flex items-center gap-2 mb-2"><Paperclip className="w-4 h-4" /> Attachments <span className="text-xs font-normal" style={{ color: "var(--text-soft)" }}>(max 6MB each)</span></Label>
                <div
                  data-testid="attach-dropzone"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all"
                  style={{
                    borderColor: dragOver ? "var(--brand)" : "var(--border)",
                    background: dragOver ? "var(--brand-dim)" : "transparent"
                  }}
                >
                  <Upload className="w-6 h-6 mx-auto mb-1" style={{ color: "var(--brand)" }} />
                  <div className="text-sm font-medium" style={{ color: "var(--text)" }}>Drop files here or click to browse</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--text-soft)" }}>PDF, images, Excel, CSV — up to 6MB</div>
                  <input ref={fileRef} type="file" multiple className="hidden" data-testid="attach-file-input" onChange={(e) => addFiles(e.target.files)} />
                </div>
                {(form.attachments || []).length > 0 && (
                  <div className="mt-3 space-y-1">
                    {form.attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg border" style={{ borderColor: "var(--border)", background: "var(--bg)" }} data-testid={`attach-${i}`}>
                        <FileText className="w-4 h-4 shrink-0" style={{ color: "var(--brand)" }} />
                        <span className="font-medium truncate flex-1" style={{ color: "var(--text)" }}>{a.name}</span>
                        <span className="text-xs font-sans" style={{ color: "var(--text-soft)" }}>{humanBytes(a.size)}</span>
                        <Button variant="ghost" size="icon" onClick={() => removeAttachment(i)}><X className="w-3 h-3" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-5">
                <Button data-testid="campaign-send-now" disabled={sendingNow} onClick={saveAndSendNow} className="text-white gap-2 bg-blue-600 hover:bg-blue-700 font-semibold shadow-xs">
                  {sendingNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span>Send Campaign Now</span>
                </Button>
                <Button data-testid="campaign-save" onClick={save} variant="outline" className="gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Save draft
                </Button>
                <Button data-testid="campaign-preview-newtab" variant="outline" onClick={openInNewTab} className="gap-2">
                  <ExternalLink className="w-4 h-4" /> Open preview
                </Button>
              </div>
            </Card>

            {/* Preview panel */}
            <div className="space-y-5">
              <Card className="card-premium p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="w-4 h-4" style={{ color: "var(--brand)" }} />
                  <h3 className="font-bold text-base" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Live preview</h3>
                  <div className="ml-auto inline-flex rounded-full p-0.5 border text-xs" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
                    <button data-testid="preview-desktop" onClick={() => setPreviewMode("desktop")} className="px-3 py-1 rounded-full flex items-center gap-1 transition-all" style={{ background: previewMode === "desktop" ? "var(--brand)" : "transparent", color: previewMode === "desktop" ? "white" : "var(--text-soft)" }}>
                      <Monitor className="w-3 h-3" /> Desktop
                    </button>
                    <button data-testid="preview-mobile" onClick={() => setPreviewMode("mobile")} className="px-3 py-1 rounded-full flex items-center gap-1 transition-all" style={{ background: previewMode === "mobile" ? "var(--brand)" : "transparent", color: previewMode === "mobile" ? "white" : "var(--text-soft)" }}>
                      <Smartphone className="w-3 h-3" /> Mobile
                    </button>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--border)", background: "#F3F4F6" }}>
                  <iframe
                    data-testid="preview-iframe"
                    title="Email preview"
                    srcDoc={previewHtml}
                    className="w-full bg-white transition-all mx-auto block"
                    style={{ height: 520, maxWidth: previewMode === "mobile" ? 380 : "100%" }}
                  />
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* AI Generate */}
        <TabsContent value="ai" className="mt-4">
          <div className="max-w-xl">
            <Card className="card-premium p-6" style={{ background: "color-mix(in srgb, var(--brand) 6%, var(--surface))", borderColor: "color-mix(in srgb, var(--brand) 25%, var(--border))" }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-dim)" }}>
                  <Sparkles className="w-5 h-5" style={{ color: "var(--brand)" }} />
                </div>
                <div>
                  <div className="font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>AI Newsletter Generator</div>
                  <div className="text-xs" style={{ color: "var(--text-soft)" }}>Powered by Gemini — generates subject, preheader & HTML</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Topic *</Label>
                  <Input data-testid="ai-topic" value={aiForm.topic} onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })} placeholder="Q1 product roadmap launch, summer sale, etc." />
                </div>
                <div>
                  <Label>Target audience</Label>
                  <Input data-testid="ai-audience" value={aiForm.audience} onChange={(e) => setAiForm({ ...aiForm, audience: e.target.value })} />
                </div>
                <div>
                  <Label>Tone</Label>
                  <Select value={aiForm.tone} onValueChange={(v) => setAiForm({ ...aiForm, tone: v })}>
                    <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="friendly and professional">Friendly & Professional</SelectItem>
                      <SelectItem value="bold and urgent">Bold & Urgent</SelectItem>
                      <SelectItem value="witty and casual">Witty & Casual</SelectItem>
                      <SelectItem value="authoritative">Authoritative</SelectItem>
                      <SelectItem value="empathetic">Empathetic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="ai-generate-newsletter" onClick={generate} disabled={generating} className="w-full text-white gap-2" style={{ background: "var(--brand)" }}>
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate with Gemini</>}
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
